// src/stores/slice/permissionSlice.js
import { fetchPermissionByUserId } from "@/pages/permission/permissionApi";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

// ─── Thunk ────────────────────────────────────────────────────────────────────
export const loadUserPermissions = createAsyncThunk(
  "permission/loadUserPermissions",
  async (userId, { rejectWithValue }) => {
    try {
      const data = await fetchPermissionByUserId(userId);
      console.log("vdatadata", data)
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// ─── Helper ───────────────────────────────────────────────────────────────────
// Shape: { [menuId]: { enabled, canView, canCreate, canEdit, canDelete, resources: { [resource_id]: {...} } } }
// permissionSlice.js — fix buildPermMap
function buildPermMap(menuPermissions = []) {
  const map = {};
  for (const mp of menuPermissions) {
    const resources = {};
    for (const rp of mp.resources || []) {
      resources[rp.resource_id] = {
        canView: rp.can_view,
        canCreate: rp.can_create,
        canEdit: rp.can_edit,
        canDelete: rp.can_delete,
      };
    }

    // ← use snake_case keys that match the actual API response
    map[mp.menu_id] = {
      enabled: mp.enabled,
      canView: mp.can_view,
      canCreate: mp.can_create,
      canEdit: mp.can_edit,
      canDelete: mp.can_delete,
      resources,
    };
  }
  return map;
}

// ─── Slice ────────────────────────────────────────────────────────────────────
const permissionSlice = createSlice({
  name: "permission",
  initialState: {
    userId: null,
    role: null,       // ← was never being set before
    isActive: false,
    rawData: null,
    permMap: {},
    status: "idle",
    error: null,
  },
  reducers: {
    clearPermissions(state) {
      state.userId = null;
      state.role = null;
      state.isActive = false;
      state.rawData = null;
      state.permMap = {};
      state.status = "idle";
      state.error = null;
    },
    patchMenuPermission(state, action) {
      const { menuId, ...changes } = action.payload;
      if (state.permMap[menuId]) {
        state.permMap[menuId] = { ...state.permMap[menuId], ...changes };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadUserPermissions.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      // Quick debug — add this temporarily in the fulfilled case
      // permissionSlice.js — fix fulfilled case
      .addCase(loadUserPermissions.fulfilled, (state, { payload }) => {
        state.status = "succeeded";
        state.isActive = payload.is_active;        // ← was payload.isActive
        state.userId = payload.user_id;          // ← was payload.userId
        state.role = payload.role || null;
        state.rawData = payload;
        state.permMap = buildPermMap(payload.menuPermissions);

        console.log("permMap sssssssssssss:", buildPermMap(payload.menuPermissions));
      })


      .addCase(loadUserPermissions.rejected, (state, { payload }) => {
        state.status = "failed";
        state.error = payload;
      });
  },
});

export const { clearPermissions, patchMenuPermission } = permissionSlice.actions;
export default permissionSlice.reducer;

// ─── Selectors ────────────────────────────────────────────────────────────────

export const selectMenuEnabled = (menuId) => (state) =>
  !!state?.permission?.permMap?.[menuId]?.enabled;

export const selectMenuAction = (menuId, action) => (state) => {
  const mp = state?.permission?.permMap?.[menuId];
  if (!mp?.enabled) return false;
  const key = `can${action.charAt(0).toUpperCase()}${action.slice(1)}`;
  return !!mp[key];
};

export const selectResourceAction = (menuId, resource_id, action) => (state) => {
  const mp = state?.permission?.permMap?.[menuId];
  if (!mp?.enabled) return false;
  const rp = mp.resources?.[resource_id];
  if (!rp) return false;
  const key = `can${action.charAt(0).toUpperCase()}${action.slice(1)}`;
  return !!rp[key];
};

export const selectMenuPermissions = (menuId) => (state) =>
  state?.permission?.permMap?.[menuId] || null;

export const selectPermissionStatus = (state) => state.permission?.status;
export const selectPermissionError = (state) => state.permission?.error;
export const selectUserRole = (state) => state.permission?.role;
export const selectRawPermissions = (state) => state.permission?.rawData;
export const selectIsActive = (state) => state.permission?.isActive;